import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import FastAPIInstance from '../../../services/risksystem3-public-api';
import {
  SaveKidDocumentPayload,
  ReviewKidDocumentPayload,
  UpdateOverviewStatusPayload,
  KidDocumentIdentifierPayload,
  CreateKidDocumentInput,
  CreateKidLayoutTemplatePayload,
  CreateKidTemplatePayload,
  UpdateKidTemplateContentPayload,
  UpdateKidTemplateNamePayload,
  DowngradeKidTemplatePayload,
  AddTemplateToDocumentPayload,
  RemoveTemplateFromDocumentPayload,
} from '../types';
import { toast } from '../../../components/toast/Toast';
import { getUploadErrorMessage } from '../../upload/upload.routes';
import {
  updateOverviewStatus,
  createKidCosts,
  CreateKidCostVariablesPayload,
  revokeReviewKid,
  calculateKidFigures,
  CalculateKidFiguresPayload,
  generateKidDocument,
  generateKidComparison,
  createKidLayoutTemplate,
  createKidTemplate,
  updateKidTemplateContent,
  updateKidTemplateName,
  downgradeKidTemplate,
  deactivateKidTemplate,
  addTemplateToDocument,
  removeTemplateFromDocument,
} from '../kids.routes';
import {
  KidComparisonData,
  KidDocumentForm,
} from '../../../components/pages/raidr/kidsV2/types/KidPdfGeneratorV2.types';
import { sanitiseFileName } from '../../../utilities/helpers/common/sanitisers';
import { extractDetail } from '../../../utilities/QueryHelpers';

const TABLE_TAGS = new Set([
  'table',
  'costs_over_time_table',
  'performance_scenarios_table',
  'composition_of_costs_table',
] as const);

function parseTableContentIfNeeded(content: unknown) {
  if (Array.isArray(content)) return content;
  if (typeof content !== 'string') return content;

  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed)) {
      return parsed;
    }
    return content;
  } catch {
    return content;
  }
}

function sanitiseKidPdfNameFields<
  T extends { fund_name: string; fund_name_full: string; share_class: string },
>(payload: T): T {
  return {
    ...payload,
    fund_name: sanitiseFileName(payload.fund_name),
    fund_name_full: sanitiseFileName(payload.fund_name_full),
    share_class: sanitiseFileName(payload.share_class),
  };
}

export function useGenerateKidDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: KidDocumentForm) => {
      const parsedElements = data.elements?.map((element: any) => {
        if (!TABLE_TAGS.has(element.tag)) {
          return element;
        }

        return {
          ...element,
          content: parseTableContentIfNeeded(element.content),
        };
      });

      const refinedPayload: KidDocumentForm = sanitiseKidPdfNameFields({
        ...data,
        elements: parsedElements ?? data.elements,
      });

      return generateKidDocument(refinedPayload);
    },
    onSuccess: (_data, _variables) => {
      // Invalidate all KID overview queries so lists refresh after PDF regeneration.
      queryClient.invalidateQueries({
        queryKey: ['kid-overview-data'],
      });
    },
  });
}

export function useGenerateKidComparison() {
  return useMutation({
    mutationFn: (compareData: KidComparisonData) => {
      const processedCompareData = sanitiseKidPdfNameFields({
        client: compareData.client,
        fund_name: compareData.fund_name,
        fund_name_full: compareData.fund_name_full,
        share_class: compareData.share_class,
        threshold: compareData.threshold,
        comparison_version: compareData.comparison_version,
        target_version: compareData.target_version,
      });

      return generateKidComparison(processedCompareData);
    },
  });
}

const saveKidDocument = async (payload: SaveKidDocumentPayload) => {
  return FastAPIInstance.put('/v1/kid/data/update', payload);
};

export function useSaveKidDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['save-kid-document'],
    mutationFn: (data: SaveKidDocumentPayload) => saveKidDocument(data),
    onError: (error) => {
      toast({
        title: 'Error',
        message: 'Failed to save KID document',
        type: 'error',
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['kid-overview-data', variables.client_id],
      });
      queryClient.invalidateQueries({
        queryKey: [
          'kid-data',
          variables.client_id,
          variables.fund_id,
          variables.share_class_id,
          variables.document_language,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template-incomplete-documents', variables.client_id],
      });
      toast({
        title: 'Success',
        message: 'KID document saved successfully',
        type: 'success',
      });
    },
  });
}

// ---- Deactivate KID Document ----

const deactivateKidDocument = async (payload: KidDocumentIdentifierPayload) => {
  const { client_id, fund_id, share_class_id, document_language } = payload;
  return FastAPIInstance.delete(
    `/v1/kid/data/${client_id}/${fund_id}/${share_class_id}`,
    {
      params: {
        document_language,
      },
    },
  );
};

export function useDeactivateKidDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['deactivate-kid-document'],
    mutationFn: (data: KidDocumentIdentifierPayload) =>
      deactivateKidDocument(data),
    onError: (error) => {
      toast({
        title: 'Error',
        message: 'Failed to deactivate KID document',
        type: 'error',
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['kid-overview-data', variables.client_id],
      });
      toast({
        title: 'Success',
        message: 'KID document deactivated successfully',
        type: 'success',
      });
    },
  });
}

// ---- Review KID Document ----

const reviewKidDocument = async (payload: ReviewKidDocumentPayload) => {
  return FastAPIInstance.post('/v1/kid/data/review', payload);
};

export function useReviewKidDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['review-kid-document'],
    mutationFn: (data: ReviewKidDocumentPayload) => reviewKidDocument(data),
    onError: () => {
      toast({
        title: 'Error',
        message: 'Failed to review KID document',
        type: 'error',
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['kid-overview-data', variables.client_id],
      });
      toast({
        title: 'Success',
        message: 'KID document reviewed successfully',
        type: 'success',
      });
    },
  });
}

// ---- Publish KID Document ----

const publishKidDocument = async (payload: KidDocumentIdentifierPayload) => {
  return FastAPIInstance.post('/v1/kid/data/publish', payload);
};

export function usePublishKidDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['publish-kid-document'],
    mutationFn: (data: KidDocumentIdentifierPayload) =>
      publishKidDocument(data),
    onError: (error) => {
      toast({
        title: 'Error',
        message: 'Failed to publish KID document',
        type: 'error',
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['kid-overview-data', variables.client_id],
      });
      toast({
        title: 'Success',
        message: 'KID document published successfully',
        type: 'success',
      });
    },
  });
}

// ---- Reactivate KID Document ----

const reactivateKidDocument = async (payload: KidDocumentIdentifierPayload) => {
  return FastAPIInstance.patch('/v1/kid/data/reactivate', payload);
};

export function useReactivateKidDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['reactivate-kid-document'],
    mutationFn: (data: KidDocumentIdentifierPayload) =>
      reactivateKidDocument(data),
    onError: (error) => {
      toast({
        title: 'Error',
        message: 'Failed to reactivate KID document',
        type: 'error',
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['kid-overview-data', variables.client_id],
      });
      toast({
        title: 'Success',
        message: 'KID document reactivated successfully',
        type: 'success',
      });
    },
  });
}

// ---- Revoke Review KID Document ----

export function useRevokeReviewKid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['revoke-review-kid'],
    mutationFn: (data: KidDocumentIdentifierPayload) => revokeReviewKid(data),
    onError: () => {
      toast({
        title: 'Error',
        message: 'Failed to revoke review',
        type: 'error',
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['kid-overview-data', variables.client_id],
      });
      toast({
        title: 'Success',
        message: 'Review revoked successfully',
        type: 'success',
      });
    },
  });
}

// ---- Update Overview Status ----

export function useUpdateOverviewStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['update-overview-status'],
    mutationFn: (data: UpdateOverviewStatusPayload) =>
      updateOverviewStatus(
        data.client_id,
        data.fund_id,
        data.share_class_id,
        data.status_change_to,
        data.document_language,
      ),
    onError: (error) => {
      toast({
        title: 'Error',
        message: 'Failed to update KID document status',
        type: 'error',
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['kid-overview-data', variables.client_id],
      });

      queryClient.invalidateQueries({
        queryKey: [
          'kid-historical-overview',
          variables.client_id,
          variables.fund_id,
          variables.share_class_id,
          variables.document_language,
        ],
      });

      toast({
        title: 'Success',
        message: 'KID document status updated successfully',
        type: 'success',
      });
    },
  });
}

const createKidDocument = async (payload: CreateKidDocumentInput) => {
  const refinedPayload = {
    client_id: payload.clientId,
    fund_id: payload.fundId,
    share_class_id: payload.shareClassId,
    document_language: payload.language,
    document_type: payload.documentType,
    template_fund_id: payload.template_fund_id,
    template_share_class_id: payload.template_share_class_id,
    template_share_class_name: payload.template_share_class_name,
    ...(payload.layout_style_id && {
      layout_style_id: payload.layout_style_id,
    }),
    ...(payload.layout_last_updated_timestamp && {
      layout_last_updated_timestamp: payload.layout_last_updated_timestamp,
    }),
  };

  return await FastAPIInstance.post('/v1/kid/data/create', refinedPayload);
};

export function useCreateKidDocumentMutation(params?: {
  clientId?: string;
  fundId?: string;
  shareClassId?: string;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ['create-kid-document', params?.fundId, params?.shareClassId],
    mutationFn: async (data: CreateKidDocumentInput) => {
      return createKidDocument(data);
    },
    onError: (error) => {
      toast({
        title: 'Error',
        message: 'Failed to create KID document',
        type: 'error',
      });
    },
    onSuccess: (_data, variables) => {
      const clientId = variables.clientId || params?.clientId;
      queryClient.invalidateQueries({
        queryKey: ['kid-overview-data', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template-incomplete-documents', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template-affected-share-classes', clientId],
      });
      toast({
        title: 'Success',
        message: 'KID document created successfully',
        type: 'success',
      });
    },
  });

  // After an error, briefly show the error state (e.g. on LoadingButton)
  // and then reset the mutation so the UI returns to its default state.
  useEffect(() => {
    if (!mutation.isError) return;
    const timer = setTimeout(() => {
      mutation.reset();
    }, 3000);
    return () => clearTimeout(timer);
  }, [mutation.isError]);

  return mutation;
}

export function useCreateKidLayoutTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['create-kid-layout-template'],
    mutationFn: (data: CreateKidLayoutTemplatePayload) =>
      createKidLayoutTemplate(data),
    onError: (error) => {
      toast({
        title: 'Could not create template',
        message: extractDetail(error) ?? 'Failed to create template',
        type: 'error',
        duration: 6000,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['kid-layout-styles', variables.client_id],
      });
      toast({
        title: 'Success',
        message: 'KID layout template created successfully',
        type: 'success',
      });
    },
  });
}

// ---- Create/Update KID Cost Variables ----

export function useCreateKidCosts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['create-kid-cost-variables'],
    mutationFn: (data: CreateKidCostVariablesPayload) => createKidCosts(data),
    onError: (error) => {
      toast({
        title: 'Error',
        message: 'Failed to create/update cost variables',
        type: 'error',
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          'kid-cost-variables',
          variables.client_id,
          variables.fund_id,
          variables.share_class_id,
        ],
      });
      toast({
        title: 'Success',
        message: 'Cost variables saved successfully',
        type: 'success',
      });
    },
  });
}

// ---- Calculate KID Figures ----

export function useCalculateKidFigures() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['calculate-kid-figures'],
    mutationFn: (data: CalculateKidFiguresPayload) => calculateKidFigures(data),
    onError: (error) => {
      toast({
        title: 'Error',
        message: getUploadErrorMessage(
          error,
          'Failed to calculate figures',
        ),
        type: 'error',
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          'kid-figures-raw',
          variables.client_id,
          variables.fund_id,
          variables.share_class_id,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          'kid-latest-figures',
          variables.client_id,
          variables.fund_id,
          variables.share_class_id,
        ],
      });
      toast({
        title: 'Success',
        message: 'Figures calculated successfully',
        type: 'success',
      });
    },
  });
}

export function useCreateKidTemplate(clientId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['create-kid-template', clientId],
    mutationFn: (payload: CreateKidTemplatePayload) =>
      createKidTemplate(clientId!, payload),
    onError: (error) => {
      toast({
        title: 'Could not create template',
        message: extractDetail(error) ?? 'Failed to create template',
        type: 'error',
        duration: 6000,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['kid-templates', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template-incomplete-documents', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template-affected-share-classes', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-overview-data', clientId],
      });
      toast({
        title: 'Success',
        message: 'Template created successfully',
        type: 'success',
      });
    },
  });
}

export function useUpdateKidTemplateContent(
  clientId?: string,
  templateId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['update-kid-template-content', clientId, templateId],
    mutationFn: (payload: UpdateKidTemplateContentPayload) =>
      updateKidTemplateContent(clientId!, templateId!, payload),
    onError: (error) => {
      toast({
        title: 'Error',
        message: extractDetail(error) ?? 'Failed to update template content',
        type: 'error',
        duration: 6000,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['kid-templates', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template', clientId, templateId],
      });
      toast({
        title: 'Success',
        message: 'Template content updated successfully',
        type: 'success',
      });
    },
  });
}

export function useUpdateKidTemplateName(
  clientId?: string,
  templateId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['update-kid-template-name', clientId, templateId],
    mutationFn: (payload: UpdateKidTemplateNamePayload) =>
      updateKidTemplateName(clientId!, templateId!, payload),
    onError: (error) => {
      toast({
        title: 'Could not rename template',
        message: extractDetail(error) ?? 'Failed to rename template',
        type: 'error',
        duration: 6000,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['kid-templates', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template', clientId, templateId],
      });
      toast({
        title: 'Success',
        message: 'Template renamed',
        type: 'success',
      });
    },
  });
}

export function useDowngradeKidTemplate(
  clientId?: string,
  templateId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['downgrade-kid-template', clientId, templateId],
    mutationFn: (payload: DowngradeKidTemplatePayload) =>
      downgradeKidTemplate(clientId!, templateId!, payload),
    onError: (error) => {
      toast({
        title: 'Could not downgrade template',
        message: extractDetail(error) ?? 'Failed to downgrade template',
        type: 'error',
        duration: 6000,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['kid-templates', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template', clientId, templateId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template-affected-share-classes', clientId, templateId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template-setup-status', clientId, templateId],
      });
      toast({
        title: 'Success',
        message: 'Template downgraded successfully',
        type: 'success',
      });
    },
  });
}

export function useDeactivateKidTemplate(
  clientId?: string,
  templateId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['deactivate-kid-template', clientId, templateId],
    mutationFn: () => deactivateKidTemplate(clientId!, templateId!),
    onError: () => {
      toast({
        title: 'Error',
        message: 'Failed to deactivate template',
        type: 'error',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['kid-templates', clientId],
      });
      queryClient.removeQueries({
        queryKey: ['kid-template', clientId, templateId],
      });
      toast({
        title: 'Success',
        message: 'Template deactivated successfully',
        type: 'success',
      });
    },
  });
}

export function useAddTemplateToDocument(clientId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['add-template-to-document', clientId],
    mutationFn: (payload: AddTemplateToDocumentPayload) =>
      addTemplateToDocument(clientId!, payload),
    onError: (error: any) => {
      toast({
        title: 'Error',
        message:
          error?.response?.data?.detail ?? 'Failed to add template to document',
        type: 'error',
        duration: 6000,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['kid-templates', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template-incomplete-documents', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template-affected-share-classes', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template-setup-status', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-overview-data', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          'kid-data',
          clientId,
          variables.fund_id,
          variables.share_class_id,
          variables.document_language,
        ],
      });
      toast({
        title: 'Success',
        message: 'Template added to document',
        type: 'success',
      });
    },
  });
}

export function useRemoveTemplateFromDocument(clientId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['remove-template-from-document', clientId],
    mutationFn: (payload: RemoveTemplateFromDocumentPayload) =>
      removeTemplateFromDocument(clientId!, payload),
    onError: (error: any) => {
      toast({
        title: 'Error',
        message:
          error?.response?.data?.detail ??
          'Failed to remove template from document',
        type: 'error',
        duration: 6000,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['kid-templates', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template-incomplete-documents', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template-affected-share-classes', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-template-setup-status', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['kid-overview-data', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          'kid-data',
          clientId,
          variables.fund_id,
          variables.share_class_id,
          variables.document_language,
        ],
      });
      toast({
        title: 'Success',
        message: 'Template removed from document',
        type: 'success',
      });
    },
  });
}
